import { useCallback, useEffect, useState } from 'react';
import ThreadPanel from './ThreadPanel';
import AssistantPanel from './AssistantPanel';
import RailSwitcher from './RailSwitcher';
import { usePageSubject } from '../contexts/PageSubjectContext';
import type { PageSubject } from '../contexts/PageSubjectContext';
import { DOCKED_RAIL_WIDTH, type RailFront } from '../contexts/AssistantRailContext';

/**
 * The one AI surface: a thread on the record, and a private assistant, side by side.
 *
 * Replaces the corner bubble (which knew only a URL path) and the full-page chat, both now
 * deleted. See docs/planning/ai-rail-design.md.
 *
 * ⭐ It lives in Layout, OUTSIDE <main>, so a streaming answer keeps streaming across a route
 * change. The full-page chat was a page - navigating away killed the answer mid-sentence.
 *
 * OO1 (2026-09-03): the shell's top bar carries the "● Assistant" toggle the design drew, so
 * the open state can now be OWNED BY LAYOUT and passed in (`open` / `onOpenChange`). Rendered
 * on its own - the unit tests do this - the rail is uncontrolled and still offers its own
 * opener, so "defaults to closed, showing only the opener" stays true of the component.
 */

/** Reused from the bubble deliberately: an operator who left the bubble open finds the rail
 *  open, rather than having their preference silently reset by a rename. */
const OPEN_KEY = 'apex-assistant-open';

/** The docked breakpoint. Kept in step with the xl: classes on the frame below - 1280px is
 *  Tailwind's xl, and this query exists only to tell JS which of those two branches is live. */
const XL_QUERY = '(min-width: 1280px)';

type Tab = 'thread' | 'assistant';

/**
 * UI2 - how a human names each record kind, so the rail says "Property · 1200 W 2nd St" rather
 * than naming a table. ⭐ Copied in spirit from `AssistantPanel.KIND_LABEL`, which already makes
 * the same call: `property` and `master_property` are both "Property", because the operator does
 * not think in terms of which table a record came from.
 */
const KIND_LABEL: Record<PageSubject['type'], string> = {
  crm_deal: 'Deal',
  property: 'Property',
  master_property: 'Property',
};

/**
 * The rail's open state, persisted under the key the bubble used. Layout calls this so the
 * top-bar toggle and the rail agree; the rail calls it itself when nothing controls it.
 */
export function useAssistantOpen() {
  const [open, setOpenState] = useState(false);

  // ⚠️ Read in an effect, not in useState's initialiser: localStorage can throw in private
  // mode, and the rail defaulting to closed is a smaller harm than the app failing to render.
  useEffect(() => {
    try { setOpenState(localStorage.getItem(OPEN_KEY) === 'true'); } catch { /* stays closed */ }
  }, []);

  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    try { localStorage.setItem(OPEN_KEY, String(next)); } catch { /* preference simply not kept */ }
  }, []);

  return { open, setOpen };
}

interface AiRailProps {
  fullBleed?: boolean;
  /** When supplied, Layout owns the state and the rail renders nothing while closed. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * ⭐ The 112th (his answer 2026-09-30, the chat beside an open letter) - from Layout's `AssistantRailContext`: a letter
   *   is open, which of the two is in front while this rail overlays it, the way back to it, and a count that moves each
   *   time the letter asks for the chat (the rail then shows its Assistant tab - the one that can change the letter).
   */
  letterOpen?: boolean;
  front?: RailFront;
  onShowLetter?: () => void;
  assistantAsked?: number;
}

export default function AiRail({
  fullBleed = false, open: controlledOpen, onOpenChange, letterOpen = false, front = 'letter', onShowLetter, assistantAsked = 0,
}: AiRailProps) {
  const subject = usePageSubject();
  const own = useAssistantOpen();
  const controlled = controlledOpen !== undefined;
  const open = controlled ? controlledOpen : own.open;
  const [tab, setTab] = useState<Tab>('thread');
  /**
   * The letter asked for the chat: show the Assistant tab. ⛔ Adjusted DURING render, not in an effect - an effect runs
   *   after the rail has painted on the Thread tab, and ThreadPanel would mount for that one frame (its fetch, its poll).
   */
  const [seenAsk, setSeenAsk] = useState(0);
  if (assistantAsked !== seenAsk) {
    setSeenAsk(assistantAsked);
    if (assistantAsked > 0) setTab('assistant');
  }
  const [switcherOpen, setSwitcherOpen] = useState(false);
  // UI3: a general chat the switcher asked AssistantPanel to load. Cleared once it is taken, so
  // picking the SAME chat twice loads it twice rather than being swallowed as "no change".
  const [openChatId, setOpenChatId] = useState<string | null>(null);
  const [atXl, setAtXl] = useState(false);
  const [viewport, setViewport] = useState<{ height: number; offsetTop: number } | null>(null);

  // 🚨 iOS DOES NOT SHRINK THE LAYOUT VIEWPORT WHEN THE SOFTWARE KEYBOARD OPENS. A
  // `position: fixed; inset: 0` sheet therefore keeps its FULL height, and both panels put
  // their composer at the bottom of a full-height flex column - so the composer ends up behind
  // the keyboard, invisible while the operator types into it. The VISUAL viewport is the part
  // that does shrink, so the sheet is sized from that instead.
  useEffect(() => {
    const v = window.visualViewport;
    if (!v) return; // older browsers keep today's behaviour rather than crashing
    const read = () => setViewport({ height: v.height, offsetTop: v.offsetTop });
    read();
    // BOTH events, not just resize: iOS fires `scroll` on the visual viewport when the keyboard
    // shifts it, which is exactly the moment offsetTop stops being 0.
    v.addEventListener('resize', read);
    v.addEventListener('scroll', read);
    return () => {
      v.removeEventListener('resize', read);
      v.removeEventListener('scroll', read);
    };
  }, []);

  // Which branch of `frame` is live, in JS. matchMedia rather than an innerWidth read so a
  // rotation or a resized window is picked up by the change event instead of being missed.
  useEffect(() => {
    const mq = typeof window.matchMedia === 'function' ? window.matchMedia(XL_QUERY) : null;
    if (!mq) return; // no matchMedia (jsdom, very old browsers): assume the overlay branch
    setAtXl(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setAtXl(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  /**
   * ⛔ `useCallback`, NOT an inline arrow. `AssistantPanel`'s loader effect lists this in its
   * dependencies, so a fresh identity every render would re-run the fetch on every render -
   * an endless reload of the chat that was just opened.
   */
  const clearOpenChat = useCallback(() => setOpenChatId(null), []);

  const toggle = (next: boolean) => {
    if (controlled) onOpenChange?.(next);
    else own.setOpen(next);
  };

  // A page with no record has no thread to show, so the assistant is the only tab.
  const activeTab: Tab = subject ? tab : 'assistant';

  // UI2: the same sentence the strip shows, for the native tooltip on a truncated label.
  const subjectLine = subject ? `${KIND_LABEL[subject.type]} · ${subject.label}` : 'General';

  if (!open) {
    // Controlled by Layout: the top bar's "Assistant" button is the opener, per the design.
    if (controlled) return null;
    return (
      <button
        onClick={() => toggle(true)}
        aria-label="Open the assistant"
        className={`fixed bottom-4 right-4 w-12 h-12 rounded-full bg-primary-500 text-white shadow-lg
                    flex items-center justify-center ${fullBleed ? 'z-[1250]' : 'z-40'}`}
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.8L3 21l1.8-4A7.96 7.96 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      </button>
    );
  }

  /**
   * 🚨 THE MAP IS THE EXCEPTION, DELIBERATELY. On a fullBleed page the rail OVERLAYS even at
   * 1280px rather than docking, because Leaflet measures its container once and resizing it
   * without invalidateSize() produces grey tiles. It also has to clear the map's z-[1000+]
   * overlays - the brochure dialog needed z-[1200] for exactly this.
   *
   * Everywhere else: full-screen sheet below 1280px, docked column at or above it. His pane
   * runs about 250px to 1280px, and a permanent column at 250px leaves the record a sliver.
   */
  // 🚨 THE DOCKED BRANCH NEEDS AN EXPLICIT HEIGHT. xl:static drops inset-0 along with the fixed
  // positioning it was pairing with, and supplies no height or sticky behavior in their place -
  // so without xl:h-[calc(var(--vh-full)-60px)] the aside collapses to <main>'s content height. On a
  // long page (dashboard, property list) that puts the composer at the bottom of the whole
  // document instead of the viewport. xl:self-start is required alongside xl:sticky: without it
  // the aside stretches to the flex line's cross size and sticky has nothing to stick within.
  // The 60px is the shell's sticky top bar (Layout.TOP_BAR_PX): the column sticks BELOW it and
  // is sized to what remains, so the bar never paints over the rail's tab strip.
  // ⭐ `--vh-full` rather than `100dvh` directly: it falls back to 100vh where dvh is
  // unsupported (see index.css). A bare dvh invalidated the declaration outright and cost the
  // prospecting map its entire height on a real phone.
  // 🚨 THE SHEET'S Z-INDEX (below xl) IS z-[1200], SANDWICHED BETWEEN TWO OF Layout.tsx's LAYERS,
  // NOT PICKED FREELY. It must be ABOVE the top bar (z-[1100] below md) - at z-40 the bar painted
  // over the sheet's own header, so document.elementFromPoint at the Thread tab's centre returned
  // the bar's hamburger svg instead of the tab; both tabs existed in the DOM but neither could be
  // clicked. It must also stay BELOW the mobile nav drawer overlay (z-[2100]) so the drawer still
  // opens over the rail instead of under it. xl:z-auto resets this back to normal stacking once
  // docked, so the rail does not float above page content there.
  // ⭐ THE 112TH: WITH A LETTER OPEN (the LOI dialog, `fixed inset-0 z-[1200]` for the map's sake) an overlaying rail is
  // either BEHIND it (z-[1150] - still above the mobile top bar's z-[1100]) or IN FRONT of it (z-[1250] - still below the
  // nav drawer's z-[2100]). Never LEVEL with it: there the DOM order decided, and a letter the chat opened on a record
  // page landed hidden under the sheet (measured at 375). Docked, the rail sits BESIDE the letter and xl:z-auto applies.
  const overlayZ = letterOpen ? (front === 'chat' ? 'z-[1250]' : 'z-[1150]') : fullBleed ? 'z-[1250]' : 'z-[1200]';
  const frame = fullBleed
    ? `fixed inset-y-0 right-0 w-full sm:w-[342px] ${overlayZ}`
    : `fixed inset-0 ${overlayZ} xl:static xl:inset-auto xl:z-auto ${DOCKED_RAIL_WIDTH} xl:flex-shrink-0 xl:sticky xl:top-[60px] xl:h-[calc(var(--vh-full)-60px)] xl:self-start`;

  // The rail is a fixed overlay on a fullBleed page at ANY width, and below xl everywhere else.
  const isOverlay = fullBleed || !atXl;

  // 🚨 THE DOCKED COLUMN IS DELIBERATELY EXCLUDED FROM BOTH OF THE FOLLOWING. It is already
  // verified working, an inline height would win over xl:h-[...] (inline styles beat any
  // class), and a docked column is not what the keyboard covers anyway. Safe-area padding is
  // likewise a notched-phone concern: it lifts the composer clear of the home indicator and
  // drops the tab strip clear of the Dynamic Island, neither of which exists beside <main>.
  //
  // ⚠️ THE SAFE-AREA PADDING IS INERT UNTIL index.html's VIEWPORT META CARRIES
  // `viewport-fit=cover`. It reads `width=device-width, initial-scale=1.0` today, and iOS
  // resolves every env(safe-area-inset-*) to 0 unless viewport-fit is cover. The padding here is
  // correct and costs nothing, but it does NOTHING on a notched iPhone until that meta changes -
  // and that change makes the WHOLE app draw edge-to-edge (Layout's top bar included), so
  // it belongs in its own commit with its own device check, not smuggled into this one.
  const overlayStyle = isOverlay && viewport
    ? { height: `${viewport.height}px`, top: `${viewport.offsetTop}px`, bottom: 'auto' }
    : undefined;
  const safeArea = isOverlay
    ? ' pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]'
    : '';

  const tabClass = (active: boolean) =>
    `-mb-px flex items-center border-b-2 px-0.5 text-[13px] leading-none transition-colors ${
      active ? 'border-primary-500 font-semibold text-gray-900' : 'border-transparent font-medium text-gray-500 hover:text-gray-800'
    }`;

  return (
    <aside
      role="complementary"
      style={overlayStyle}
      className={`${frame}${safeArea} bg-white border-l border-gray-200 flex flex-col`}
    >
      {/* The design's rail head: 60px, the tabs underlined in KW red, the close at the right. */}
      <div className="flex h-[60px] shrink-0 items-stretch gap-3.5 border-b border-gray-200 px-[18px]">
        <div role="tablist" className="flex items-stretch gap-3.5">
          {subject && (
            <button
              role="tab"
              aria-selected={activeTab === 'thread'}
              onClick={() => setTab('thread')}
              className={tabClass(activeTab === 'thread')}
            >
              Thread
            </button>
          )}
          <button
            role="tab"
            aria-selected={activeTab === 'assistant'}
            onClick={() => setTab('assistant')}
            className={tabClass(activeTab === 'assistant')}
          >
            Assistant
          </button>
        </div>
        <button
          onClick={() => toggle(false)}
          aria-label="Close the assistant"
          className="ml-auto flex h-[26px] w-[26px] self-center items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-900"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* ⭐ THE 112TH: the chat in front of an open letter (the sheet below 1280, the map page's overlay at any width) - the
            way back to the letter WITHOUT closing the rail, which would unmount the chat and lose this conversation. */}
      {letterOpen && front === 'chat' && isOverlay && onShowLetter && (
        <button
          type="button"
          onClick={() => onShowLetter()}
          className="flex w-full shrink-0 items-center gap-2 border-b border-gray-200 bg-gray-50 px-[18px] py-2.5 text-left text-[12.5px] font-semibold text-gray-900 hover:bg-gray-100"
        >
          <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to the letter
        </button>
      )}

      {/*
        ===== UI2: the rail says what it is referencing =====
        His words: *"when I'm in the thread/assistant, it would be good if it tells me what
        property I'm referencing, just to keep it easy to understand."*

        ⭐⭐ IT LIVES IN THE RAIL HEADER, NOT IN A PANEL, because it is true of BOTH tabs and he
        named both. `AssistantPanel` already carried an `About: {label}` line and `ThreadPanel`
        carried nothing but the word "Conversation" - so the Assistant tab answered his question
        and the Thread tab, the one actually bound to the property, did not. One line above both
        tabs answers it once and cannot disagree with itself.

        ===== UI3: that same strip is the switcher =====
        ⭐⭐⭐ ONE CONTROL, TWO JOBS - which is why these shipped together. The name of the thing
        you are looking at is exactly the control you press to look at something else, so the
        rail spends no extra vertical space on a switcher, in a column that is 342px wide and a
        full-screen sheet on a phone.
      */}
      <div className="shrink-0 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setSwitcherOpen((v) => !v)}
          aria-expanded={switcherOpen}
          aria-label="Switch conversation"
          className="flex w-full items-center gap-2 px-[18px] py-2 text-left hover:bg-gray-50"
        >
          {/* min-w-0 + truncate: a long address must ellipsis rather than push the chevron out
              of the rail. The full label stays reachable by title on a desktop. */}
          <span className="min-w-0 flex-1 truncate text-[12.5px] text-gray-600" title={subjectLine}>
            {subject ? (
              <>
                <span className="text-gray-500">{KIND_LABEL[subject.type]} · </span>
                <span className="font-semibold text-gray-900">{subject.label}</span>
              </>
            ) : (
              /* ⭐ "General" is the app's OWN word for a page with no record - `AssistantPanel`
                 used it here before UI2 moved this line up - and it is also his word in UI3
                 ("general threads"). Keeping it makes the switcher's second section self-explanatory. */
              <span className="text-gray-500">General</span>
            )}
          </span>
          <svg
            className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${switcherOpen ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/*
        🚨 ASYMMETRIC ON PURPOSE - do not "fix" it into symmetry.
        ThreadPanel polls every 20 seconds, and the rail is mounted on EVERY page, so ThreadPanel
        is only ever mounted while its own tab is active - a hidden-but-mounted copy sitting on
        every page would turn a per-page poll into an app-wide one.
        AssistantPanel has no timer and issues a request only when the operator presses Send
        (verified: no useEffect, no interval), so keeping it mounted costs nothing. It stays
        MOUNTED for as long as the rail is OPEN, and is merely hidden with CSS when the thread
        tab is showing. That is load-bearing, not cosmetic: a page whose record loads
        asynchronously can still have a null subject when the operator opens the rail, so the
        assistant is the only tab. If they start a conversation there and the page THEN publishes
        its subject, activeTab flips to 'thread' on the next render - conditionally rendering the
        assistant would unmount it on exactly that transition, destroying its messages, draft,
        chatId and any in-flight streaming answer. That is the same loss the top-of-file comment
        on this component says the rail exists to avoid.
      */}
      <div className="relative flex-1 min-h-0 overflow-hidden" data-testid="rail-body">
        {/*
          🚨 THE OPEN LIST FLOATS OVER THE CHAT - it is never laid out above it (his phone, 2026-10-27).
          Drawn in the page's flow, the list took the chat's height: the chat's fixed rows (Read aloud,
          Voice, Voice chat, the "earlier chat" banner) do not shrink, so the typing box was cut off below
          the sheet, and a tap where it should be made iOS scroll the whole page to reach it - the page
          behind slid into view. Here the chat keeps every row it had, the list scrolls inside this area,
          and a tap anywhere outside it closes it.
        */}
        {switcherOpen && (
          <>
            <button
              type="button"
              aria-label="Close the conversation list"
              onClick={() => setSwitcherOpen(false)}
              className="absolute inset-0 z-20 cursor-default bg-gray-900/10"
            />
            <div
              data-testid="rail-switcher-float"
              className="absolute inset-x-0 top-0 z-30 max-h-full overflow-y-auto shadow-lg"
            >
              <RailSwitcher
                currentSubjectId={subject?.id ?? null}
                currentSubjectType={subject?.type ?? null}
                onClose={() => setSwitcherOpen(false)}
                onOpenChat={(id) => {
                  // ⛔ THE TAB MOVES WITH THE CHAT. A general chat lands in the Assistant tab, so
                  // opening one while the Thread tab is showing would load it somewhere he cannot see
                  // and read as nothing having happened.
                  setOpenChatId(id);
                  setTab('assistant');
                }}
              />
            </div>
          </>
        )}
        {activeTab === 'thread' && subject && (
          <ThreadPanel subjectType={subject.type} subjectId={subject.id} />
        )}
        <div className={activeTab === 'thread' && subject ? 'hidden h-full' : 'h-full'}>
          {/* Only an OVERLAY rail closes on History: the sheet below xl, and the map page's
              fullBleed overlay (which stays an overlay even at xl), both cover the /ai
              destination they navigate to, so staying open would strand the operator behind
              their own rail (Master Joseph, on his phone: "so i still cant see the
              history"). A DOCKED rail sits beside the content - /ai renders right next to
              it - so closing there would be an unrelated surprise. Routed through toggle(),
              the SAME close path the X button uses, so open/closed persists identically
              either way - not a second way to close. */}
          <AssistantPanel
            onNavigateAway={isOverlay ? () => toggle(false) : undefined}
            openChatId={openChatId}
            onChatOpened={clearOpenChat}
            hidden={activeTab === 'thread' && !!subject}
          />
        </div>
      </div>
    </aside>
  );
}
